/**
 * @license
 * Copyright 2025 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

// Code generated by the Google Gen AI SDK generator DO NOT EDIT.

import {ApiClient} from './_api_client.js';
import * as common from './_common.js';
import {BaseModule} from './_common.js';
import * as converters from './converters/_filesearchstores_converters.js';
import {Documents} from './documents.js';
import {PagedItem, Pager} from './pagers.js';
import * as types from './types.js';

export class FileSearchStores extends BaseModule {
  constructor(
    private readonly apiClient: ApiClient,
    public readonly documents: Documents = new Documents(apiClient),
  ) {
    super();
  }

  /**
   * Lists file search stores.
   *
   * @param params - The parameters for the list request.
   * @return - A pager of file search stores.
   *
   * @example
   * ```ts
   * const fileSearchStores = await ai.fileSearchStores.list({config: {'pageSize': 2}});
   * for await (const fileSearchStore of fileSearchStores) {
   *   console.log(fileSearchStore);
   * }
   * ```
   */

  list = async (
    params: types.ListFileSearchStoresParameters = {},
  ): Promise<Pager<types.FileSearchStore>> => {
    return new Pager<types.FileSearchStore>(
      PagedItem.PAGED_ITEM_FILE_SEARCH_STORES,
      (x: types.ListFileSearchStoresParameters) => this.listInternal(x),
      await this.listInternal(params),
      params,
    );
  };

  /**
   * Uploads a file asynchronously to a given File Search Store.
   * This method is not available in Gemini Enterprise Agent Platform (previously known as Vertex AI).
   * Supported upload sources:
   * - Node.js: File path (string) or Blob object.
   * - Browser: Blob object (e.g., File).
   *
   * @remarks
   * The `mimeType` can be specified in the `config` parameter. If omitted:
   *  - For file path (string) inputs, the `mimeType` will be inferred from the
   *     file extension.
   *  - For Blob object inputs, the `mimeType` will be set to the Blob's `type`
   *     property.
   *
   * This section can contain multiple paragraphs and code examples.
   *
   * @param params - Optional parameters specified in the
   *        `types.UploadToFileSearchStoreParameters` interface.
   *         @see {@link types.UploadToFileSearchStoreParameters#config} for the optional
   *         config in the parameters.
   * @return A promise that resolves to a long running operation.
   * @throws An error if called on a Gemini Enterprise Agent Platform (previously known as Vertex AI) client.
   * @throws An error if the `mimeType` is not provided and can not be inferred,
   * the `mimeType` can be provided in the `params.config` parameter.
   * @throws An error occurs if a suitable upload location cannot be established.
   *
   * @example
   * The following code uploads a file to a given file search store.
   *
   * ```ts
   * const operation = await ai.fileSearchStores.upload({fileSearchStoreName: 'fileSearchStores/foo-bar', file: 'file.txt', config: {
   *   mimeType: 'text/plain',
   * }});
   * console.log(operation.name);
   * ```
   */
  async uploadToFileSearchStore(
    params: types.UploadToFileSearchStoreParameters,
  ): Promise<types.UploadToFileSearchStoreOperation> {
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'Gemini Enterprise Agent Platform (previously known as Vertex AI) does not support uploading files to a file search store.',
      );
    }

    return this.apiClient.uploadFileToFileSearchStore(
      params.fileSearchStoreName,
      params.file,
      params.config,
    );
  }

  /**
   * Downloads media using a Media ID or URI.
   * This method is only supported in the Gemini Developer client.
   *
   * @param uri - The URI or Media ID of the blob.
   * @param config - Optional configuration for the download.
   * @returns A promise that resolves to the blob data as a Uint8Array.
   */
  async downloadMedia(
    uri: string,
    config?: types.DownloadMediaConfig,
  ): Promise<Uint8Array> {
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported in the Gemini Developer client.',
      );
    }

    const parsedUri = new URL(uri, 'http://dummy.com');
    let pathname = parsedUri.pathname;
    if (pathname.startsWith('/')) {
      pathname = pathname.slice(1);
    }

    if (!pathname.includes('/media/')) {
      throw new Error(
        `Invalid uri format: ${uri}. Expected to contain /media/`,
      );
    }

    const queryParams: Record<string, string> = {};
    parsedUri.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });
    queryParams['alt'] = 'media';

    const httpOptions: types.HttpOptions = {
      ...config?.httpOptions,
    };

    const response = await this.apiClient.request({
      path: pathname,
      httpMethod: 'GET',
      queryParams: queryParams,
      httpOptions: httpOptions,
    });

    if (response instanceof types.HttpResponse) {
      const arrayBuffer = await response.responseInternal.arrayBuffer();
      return new Uint8Array(arrayBuffer);
    } else {
      throw new Error('Unexpected response type from downloadMedia');
    }
  }

  /**
   * Creates a File Search Store.
   *
   * @param params - The parameters for creating a File Search Store.
   * @return FileSearchStore.
   */

  async create(
    params: types.CreateFileSearchStoreParameters,
  ): Promise<types.FileSearchStore> {
    let response: Promise<types.FileSearchStore>;

    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.createFileSearchStoreParametersToMldev(
        this.apiClient,
        params,
      );
      path = common.formatMap(
        'fileSearchStores',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      response = this.apiClient
        .request({
          path: path,
          queryParams: queryParams,
          body: JSON.stringify(body),
          httpMethod: 'POST',
          httpOptions: params.config?.httpOptions,
          abortSignal: params.config?.abortSignal,
        })
        .then((httpResponse) => {
          return httpResponse.json();
        }) as Promise<types.FileSearchStore>;

      return response.then((resp) => {
        return resp as types.FileSearchStore;
      });
    }
  }

  /**
   * Gets a File Search Store.
   *
   * @param params - The parameters for getting a File Search Store.
   * @return FileSearchStore.
   */

  async get(
    params: types.GetFileSearchStoreParameters,
  ): Promise<types.FileSearchStore> {
    let response: Promise<types.FileSearchStore>;

    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.getFileSearchStoreParametersToMldev(params);
      path = common.formatMap(
        '{name}',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      response = this.apiClient
        .request({
          path: path,
          queryParams: queryParams,
          body: JSON.stringify(body),
          httpMethod: 'GET',
          httpOptions: params.config?.httpOptions,
          abortSignal: params.config?.abortSignal,
        })
        .then((httpResponse) => {
          return httpResponse.json();
        }) as Promise<types.FileSearchStore>;

      return response.then((resp) => {
        return resp as types.FileSearchStore;
      });
    }
  }

  /**
   * Deletes a File Search Store.
   *
   * @param params - The parameters for deleting a File Search Store.
   */

  async delete(params: types.DeleteFileSearchStoreParameters): Promise<void> {
    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.deleteFileSearchStoreParametersToMldev(params);
      path = common.formatMap(
        '{name}',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      await this.apiClient.request({
        path: path,
        queryParams: queryParams,
        body: JSON.stringify(body),
        httpMethod: 'DELETE',
        httpOptions: params.config?.httpOptions,
        abortSignal: params.config?.abortSignal,
      });
    }
  }

  private async listInternal(
    params: types.ListFileSearchStoresParameters,
  ): Promise<types.ListFileSearchStoresResponse> {
    let response: Promise<types.ListFileSearchStoresResponse>;

    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.listFileSearchStoresParametersToMldev(params);
      path = common.formatMap(
        'fileSearchStores',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      response = this.apiClient
        .request({
          path: path,
          queryParams: queryParams,
          body: JSON.stringify(body),
          httpMethod: 'GET',
          httpOptions: params.config?.httpOptions,
          abortSignal: params.config?.abortSignal,
        })
        .then((httpResponse) => {
          return httpResponse.json();
        }) as Promise<types.ListFileSearchStoresResponse>;

      return response.then((apiResponse) => {
        const resp =
          converters.listFileSearchStoresResponseFromMldev(apiResponse);
        const typedResp = new types.ListFileSearchStoresResponse();
        Object.assign(typedResp, resp);
        return typedResp;
      });
    }
  }

  private async uploadToFileSearchStoreInternal(
    params: types.UploadToFileSearchStoreParameters,
  ): Promise<types.UploadToFileSearchStoreResumableResponse> {
    let response: Promise<types.UploadToFileSearchStoreResumableResponse>;

    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.uploadToFileSearchStoreParametersToMldev(params);
      path = common.formatMap(
        'upload/v1beta/{file_search_store_name}:uploadToFileSearchStore',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      response = this.apiClient
        .request({
          path: path,
          queryParams: queryParams,
          body: JSON.stringify(body),
          httpMethod: 'POST',
          httpOptions: params.config?.httpOptions,
          abortSignal: params.config?.abortSignal,
        })
        .then((httpResponse) => {
          return httpResponse.json();
        }) as Promise<types.UploadToFileSearchStoreResumableResponse>;

      return response.then((apiResponse) => {
        const resp =
          converters.uploadToFileSearchStoreResumableResponseFromMldev(
            apiResponse,
          );
        const typedResp = new types.UploadToFileSearchStoreResumableResponse();
        Object.assign(typedResp, resp);
        return typedResp;
      });
    }
  }

  /**
   * Imports a File from File Service to a FileSearchStore.
   *
   * This is a long-running operation, see aip.dev/151
   *
   * @param params - The parameters for importing a file to a file search store.
   * @return ImportFileOperation.
   */

  async importFile(
    params: types.ImportFileParameters,
  ): Promise<types.ImportFileOperation> {
    let response: Promise<types.ImportFileOperation>;

    let path: string = '';
    let queryParams: Record<string, string> = {};
    if (this.apiClient.isVertexAI()) {
      throw new Error(
        'This method is only supported by the Gemini Developer API.',
      );
    } else {
      const body = converters.importFileParametersToMldev(params);
      path = common.formatMap(
        '{file_search_store_name}:importFile',
        body['_url'] as Record<string, unknown>,
      );
      queryParams = body['_query'] as Record<string, string>;
      delete body['_url'];
      delete body['_query'];

      response = this.apiClient
        .request({
          path: path,
          queryParams: queryParams,
          body: JSON.stringify(body),
          httpMethod: 'POST',
          httpOptions: params.config?.httpOptions,
          abortSignal: params.config?.abortSignal,
        })
        .then((httpResponse) => {
          return httpResponse.json();
        }) as Promise<types.ImportFileOperation>;

      return response.then((apiResponse) => {
        const resp = converters.importFileOperationFromMldev(apiResponse);
        const typedResp = new types.ImportFileOperation();
        Object.assign(typedResp, resp);
        return typedResp;
      });
    }
  }
}
